<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <h1 class="mb-2">
                    <span class="text-gradient">Transfer Analytics</span>
                </h1>
                <p class="text-muted mb-3">Token transfer volume, program breakdown, large transfers, and CEX flow tracking across Solana</p>
                <div class="d-flex gap-3 align-items-center flex-wrap">
                    <date-range-picker default-range="7d" @change="handleDateRangeChange" />
                </div>
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat">
                <div class="hero-label">Transfer Volume</div>
                <div class="hero-value">${{ formatCompact(summary.totalVolume) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Total Transfers</div>
                <div class="hero-value">{{ formatCompact(summary.totalTransfers) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Unique Senders</div>
                <div class="hero-value">{{ formatCompact(summary.totalSigners) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--cyan">
                <div class="hero-label">Unique Tokens</div>
                <div class="hero-value">{{ formatCompact(summary.totalTokens) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">CEX Net Flow</div>
                <div class="hero-value" :class="cexNetFlowClass">
                    {{ cexNetFlowDirection }}${{ formatCompact(Math.abs(cexSummary.netFlow || 0)) }}
                </div>
                <div class="hero-subtitle">{{ cexNetFlowLabel }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--purple">
                <div class="hero-label">Programs</div>
                <div class="hero-value">{{ summary.programCount || 0 }}</div>
            </div>
        </div>

        <!-- Loading / Error -->
        <div v-if="loading && !timeSeries.length" class="text-center text-muted py-5">
            <i class="fas fa-spinner fa-spin me-2"></i> Loading transfer data...
        </div>
        <div v-if="error" class="alert alert-danger mx-2 mb-4">
            {{ error }}
        </div>

        <!-- SECTION: Volume Over Time -->
        <div class="section-divider">
            <span class="section-title">Transfer Volume Over Time</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Volume by Program (Stacked)</div>
                    <div class="panel-subtitle">Top programs by transfer volume</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="stackedVolumeChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Transfers &amp; Senders</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="transfersChart"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Program Breakdown + Token Breakdown -->
        <div class="section-divider">
            <span class="section-title">Breakdown</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <!-- Program breakdown -->
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">By Program</div>
                    <div class="panel-subtitle">Volume distribution across programs</div>
                </div>
                <div class="panel-body">
                    <div v-if="programs.length === 0" class="text-center text-muted py-4">
                        No program data available
                    </div>
                    <div v-else>
                        <div class="mb-3" style="height: 250px;">
                            <canvas ref="programDoughnutChart"></canvas>
                        </div>
                        <div class="table-responsive" style="max-height: 280px;">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>Program</th>
                                        <th class="text-end">Volume</th>
                                        <th class="text-end">Share</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, idx) in programs.slice(0, 10)" :key="item.program">
                                        <td>
                                            <span class="rank-badge">{{ idx + 1 }}</span>
                                            <div class="entity-info">
                                                <img v-if="item.logo" :src="item.logo" class="entity-logo" @error="$event.target.style.display='none'" />
                                                <a :href="`/analytics/programs/${item.program}`" class="text-gradient">
                                                    {{ item.name || formatPubkey(item.program) }}
                                                </a>
                                            </div>
                                        </td>
                                        <td class="text-end font-mono-num">${{ formatCompact(item.volume) }}</td>
                                        <td class="text-end">
                                            <div class="progress-inline">
                                                <div class="progress-bar" :style="{ width: `${item.share}%` }"></div>
                                                <span>{{ item.share }}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Token breakdown -->
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">By Token</div>
                    <div class="panel-subtitle">Top tokens by transfer volume</div>
                </div>
                <div class="panel-body">
                    <div v-if="tokenBreakdown.length === 0" class="text-center text-muted py-4">
                        No token data available
                    </div>
                    <div v-else>
                        <div class="mb-3" style="height: 250px;">
                            <canvas ref="tokenDoughnutChart"></canvas>
                        </div>
                        <div class="table-responsive" style="max-height: 280px;">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>Token</th>
                                        <th class="text-end">Volume</th>
                                        <th class="text-end">Transfers</th>
                                        <th class="text-end">Share</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(tok, idx) in tokenBreakdown" :key="tok.mint">
                                        <td>
                                            <span class="rank-badge">{{ idx + 1 }}</span>
                                            <div class="entity-info">
                                                <img v-if="tok.logo" :src="tok.logo" class="entity-logo" @error="$event.target.style.display='none'" />
                                                <a :href="`/tokens/${tok.mint}`" class="text-gradient">
                                                    {{ tok.symbol }}
                                                </a>
                                            </div>
                                        </td>
                                        <td class="text-end font-mono-num">${{ formatCompact(tok.volume) }}</td>
                                        <td class="text-end font-mono-num">{{ formatCompact(tok.transfers) }}</td>
                                        <td class="text-end">
                                            <div class="progress-inline">
                                                <div class="progress-bar" :style="{ width: `${tok.share}%` }"></div>
                                                <span>{{ tok.share }}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: CEX Flow Tracker -->
        <div class="section-divider">
            <span class="section-title">CEX Flow Tracker</span>
        </div>
        <!-- CEX summary cards -->
        <div class="analytics-row cols-3 mb-4">
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="big-metric text-success">${{ formatCompact(cexSummary.totalInflow) }}</div>
                    <div class="metric-label">Deposits to Exchanges</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="big-metric text-danger">${{ formatCompact(cexSummary.totalOutflow) }}</div>
                    <div class="metric-label">Withdrawals from Exchanges</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="big-metric" :class="cexNetFlowClass">
                        {{ cexNetFlowDirection }}${{ formatCompact(Math.abs(cexSummary.netFlow || 0)) }}
                    </div>
                    <div class="metric-label">{{ cexNetFlowLabel }}</div>
                </div>
            </div>
        </div>

        <!-- CEX flow time series chart -->
        <div v-if="cexTimeSeries.length > 0" class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">CEX Flow Over Time</div>
                    <div class="panel-subtitle">Deposits (green) vs Withdrawals (red), Net flow line</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="cexTimeSeriesChart"></canvas>
                </div>
            </div>
        </div>

        <!-- CEX by Exchange + by Token -->
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">By Exchange</div>
                    <div class="panel-subtitle">{{ cexSummary.walletCount || 0 }} wallets across {{ cexSummary.exchangeCount || 0 }} exchanges</div>
                </div>
                <div class="panel-body">
                    <div v-if="cexExchanges.length === 0" class="text-center text-muted py-4">
                        No CEX flow data available
                    </div>
                    <div v-else class="table-responsive" style="max-height: 400px;">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th>Exchange</th>
                                    <th class="text-end">Deposits</th>
                                    <th class="text-end">Withdrawals</th>
                                    <th class="text-end">Net Flow</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="cex in cexExchanges" :key="cex.entity">
                                    <td>
                                        <div class="entity-info">
                                            <img v-if="cex.logo" :src="cex.logo" class="entity-logo" @error="$event.target.style.display='none'" />
                                            <div class="entity-details">
                                                <span class="fw-medium">{{ cex.entity }}</span>
                                                <span class="entity-pubkey">{{ cex.wallets }} wallet{{ cex.wallets > 1 ? 's' : '' }}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-end font-mono-num text-success">${{ formatCompact(cex.inflow) }}</td>
                                    <td class="text-end font-mono-num text-danger">${{ formatCompact(cex.outflow) }}</td>
                                    <td class="text-end font-mono-num" :class="cex.netFlow >= 0 ? 'text-success' : 'text-danger'">
                                        {{ cex.netFlow >= 0 ? '+' : '-' }}${{ formatCompact(Math.abs(cex.netFlow)) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">By Token</div>
                    <div class="panel-subtitle">What tokens are flowing through exchanges</div>
                </div>
                <div class="panel-body">
                    <div v-if="cexTokens.length === 0" class="text-center text-muted py-4">
                        No token flow data available
                    </div>
                    <div v-else class="table-responsive" style="max-height: 400px;">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th>Token</th>
                                    <th class="text-end">Deposits</th>
                                    <th class="text-end">Withdrawals</th>
                                    <th class="text-end">Net Flow</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="tok in cexTokens" :key="tok.mint">
                                    <td>
                                        <div class="entity-info">
                                            <img v-if="tok.logo" :src="tok.logo" class="entity-logo" @error="$event.target.style.display='none'" />
                                            <a :href="`/tokens/${tok.mint}`" class="text-gradient">{{ tok.symbol }}</a>
                                        </div>
                                    </td>
                                    <td class="text-end font-mono-num text-success">${{ formatCompact(tok.inflow) }}</td>
                                    <td class="text-end font-mono-num text-danger">${{ formatCompact(tok.outflow) }}</td>
                                    <td class="text-end font-mono-num" :class="tok.netFlow >= 0 ? 'text-success' : 'text-danger'">
                                        {{ tok.netFlow >= 0 ? '+' : '-' }}${{ formatCompact(Math.abs(tok.netFlow)) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Large Transfers -->
        <div class="section-divider">
            <span class="section-title">Large Transfers (>$100K)</span>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Recent Large Transfers</div>
                    <div class="panel-subtitle">{{ largeTransfers.total || 0 }} transfers found</div>
                    <div class="panel-controls">
                        <select v-model="ltSort" @change="loadLargeTransfers" class="compass-select compass-select--sm">
                            <option value="timestamp">Most Recent</option>
                            <option value="amount">Largest First</option>
                        </select>
                    </div>
                </div>
                <div class="panel-body">
                    <div v-if="largeTransfers.transfers.length === 0" class="text-center text-muted py-4">
                        No large transfers found in this period
                    </div>
                    <div v-else>
                        <div class="table-responsive">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>Token</th>
                                        <th>From</th>
                                        <th>To</th>
                                        <th class="text-end">Amount</th>
                                        <th class="text-end">USD Value</th>
                                        <th>Tx</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="tx in largeTransfers.transfers" :key="tx.signature">
                                        <td class="text-nowrap">{{ formatTime(tx.timestamp) }}</td>
                                        <td>
                                            <div class="entity-info">
                                                <img v-if="tx.tokenLogo" :src="tx.tokenLogo" class="entity-logo" @error="$event.target.style.display='none'" />
                                                <a v-if="tx.mint" :href="`/tokens/${tx.mint}`" class="text-gradient">
                                                    {{ tx.tokenName || formatPubkey(tx.mint) }}
                                                </a>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="address-cell">
                                                <a :href="`/address/${tx.sender}`" class="address-link">
                                                    {{ tx.senderName || formatPubkey(tx.sender) }}
                                                </a>
                                                <span v-if="tx.senderEntity" class="entity-tag">{{ tx.senderEntity }}</span>
                                                <span v-for="label in (tx.senderLabels || []).slice(0, 2)" :key="label"
                                                      :class="['label-tag', getLabelClass(label)]">{{ label }}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="address-cell">
                                                <a :href="`/address/${tx.receiver}`" class="address-link">
                                                    {{ tx.receiverName || formatPubkey(tx.receiver) }}
                                                </a>
                                                <span v-if="tx.receiverEntity" class="entity-tag">{{ tx.receiverEntity }}</span>
                                                <span v-for="label in (tx.receiverLabels || []).slice(0, 2)" :key="label"
                                                      :class="['label-tag', getLabelClass(label)]">{{ label }}</span>
                                            </div>
                                        </td>
                                        <td class="text-end font-mono-num">{{ formatCompact(tx.amount) }}</td>
                                        <td class="text-end font-mono-num">${{ formatCompact(tx.amountUsd) }}</td>
                                        <td>
                                            <a v-if="tx.signature" :href="`https://solscan.io/tx/${tx.signature}`"
                                               target="_blank" rel="noopener" class="tx-link" title="View on Solscan">
                                                {{ formatPubkey(tx.signature) }}
                                            </a>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <!-- Pagination -->
                        <div v-if="largeTransfers.totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
                            <span class="text-muted small">
                                Page {{ largeTransfers.page }} of {{ largeTransfers.totalPages }}
                            </span>
                            <div class="d-flex gap-2">
                                <button class="compass-btn compass-btn--outline compass-btn--sm"
                                        :disabled="largeTransfers.page <= 1"
                                        @click="changeLtPage(largeTransfers.page - 1)">Prev</button>
                                <button class="compass-btn compass-btn--outline compass-btn--sm"
                                        :disabled="largeTransfers.page >= largeTransfers.totalPages"
                                        @click="changeLtPage(largeTransfers.page + 1)">Next</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Time Series Table -->
        <div class="section-divider">
            <span class="section-title">Time Series Breakdown</span>
        </div>
        <div class="analytics-row cols-1">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Raw Data</div>
                    <div class="panel-controls">
                        <button class="compass-btn compass-btn--outline compass-btn--sm" @click="exportData">Export CSV</button>
                    </div>
                </div>
                <div class="panel-body">
                    <div v-if="timeSeries.length === 0" class="text-center text-muted py-4">
                        No time series data available
                    </div>
                    <div v-else class="table-responsive" style="max-height: 400px;">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th class="text-end">Volume</th>
                                    <th class="text-end">Transfers</th>
                                    <th class="text-end">Senders</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in timeSeries" :key="row.timestamp">
                                    <td>{{ row.label }}</td>
                                    <td class="text-end font-mono-num">${{ formatCompact(row.volume) }}</td>
                                    <td class="text-end font-mono-num">{{ formatCompact(row.transfers) }}</td>
                                    <td class="text-end font-mono-num">{{ formatCompact(row.signers) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { getLineChartOptions, getStackedBarChartOptions, getDoughnutChartOptions, getBarChartOptions } from '../composables/useChartConfig';

export default {
    name: 'TransferAnalytics',

    data() {
        return {
            currentRange: '7d',
            loading: false,
            error: null,
            summary: {},
            timeSeries: [],
            stackedPrograms: [],
            programs: [],
            tokenBreakdown: [],
            largeTransfers: { transfers: [], total: 0, page: 1, totalPages: 1 },
            cexExchanges: [],
            cexTokens: [],
            cexTimeSeries: [],
            cexSummary: { totalInflow: 0, totalOutflow: 0, netFlow: 0, exchangeCount: 0, walletCount: 0 },
            ltSort: 'timestamp',
            ltPage: 1,
            refreshInterval: null
        };
    },

    created() {
        this.charts = {};
    },

    computed: {
        cexNetFlowClass() {
            const net = this.cexSummary.netFlow || 0;
            if (net > 0) return 'text-success';
            if (net < 0) return 'text-danger';
            return 'text-muted';
        },
        cexNetFlowDirection() {
            const net = this.cexSummary.netFlow || 0;
            return net >= 0 ? '+' : '-';
        },
        cexNetFlowLabel() {
            const net = this.cexSummary.netFlow || 0;
            return net >= 0 ? 'Net inflow (bullish)' : 'Net outflow (bearish)';
        }
    },

    mounted() {
        this.initCharts();
        this.loadData();
        this.refreshInterval = setInterval(() => this.loadData(), 60000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    },

    methods: {
        handleDateRangeChange(data) {
            this.currentRange = data.range;
            this.ltPage = 1;
            this.loadData();
        },

        initCharts() {
            this.charts.stackedVolume = new Chart(this.$refs.stackedVolumeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({ formatCallback: v => '$' + this.formatCompact(v) })
            });

            this.charts.transfers = new Chart(this.$refs.transfersChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({ formatCallback: v => this.formatCompact(v) }),
                    scales: {
                        y1: { type: 'linear', position: 'left', ticks: { callback: v => this.formatCompact(v) }, min: 0 },
                        y2: { type: 'linear', position: 'right', ticks: { callback: v => this.formatCompact(v) }, grid: { drawOnChartArea: false }, min: 0 }
                    }
                }
            });

            this.charts.programDoughnut = new Chart(this.$refs.programDoughnutChart, {
                type: 'doughnut',
                data: { labels: [], datasets: [] },
                options: getDoughnutChartOptions({ legendPosition: 'right' })
            });

            this.charts.tokenDoughnut = new Chart(this.$refs.tokenDoughnutChart, {
                type: 'doughnut',
                data: { labels: [], datasets: [] },
                options: getDoughnutChartOptions({ legendPosition: 'right' })
            });
        },

        async loadData() {
            this.loading = true;
            this.error = null;
            try {
                const range = this.capRange(this.currentRange, '30d');

                const [metricsRes, cexRes] = await Promise.all([
                    fetch(`/analytics/api/transfer-metrics?range=${range}&limit=20`),
                    fetch(`/analytics/api/cex-flows?range=${range}`)
                ]);

                if (!metricsRes.ok) throw new Error(`Transfer metrics: ${metricsRes.status}`);
                if (!cexRes.ok) throw new Error(`CEX flows: ${cexRes.status}`);

                const metricsData = await metricsRes.json();
                this.summary = metricsData.summary || {};
                this.timeSeries = metricsData.timeSeries || [];
                this.stackedPrograms = metricsData.stackedPrograms || [];
                this.programs = metricsData.programs || [];
                this.tokenBreakdown = metricsData.tokenBreakdown || [];

                const cexData = await cexRes.json();
                this.cexExchanges = cexData.exchanges || [];
                this.cexTokens = cexData.byToken || [];
                this.cexTimeSeries = cexData.timeSeries || [];
                this.cexSummary = cexData.summary || { totalInflow: 0, totalOutflow: 0, netFlow: 0 };

                this.updateCharts();

                // Load large transfers separately (can be slower)
                this.loadLargeTransfers();
            } catch (e) {
                console.error('Error loading transfer data:', e);
                this.error = 'Failed to load transfer data. Please try again.';
            } finally {
                this.loading = false;
            }
        },

        async loadLargeTransfers() {
            try {
                const range = this.capRange(this.currentRange, '30d');
                const res = await fetch(
                    `/analytics/api/large-transfers?range=${range}&sort=${this.ltSort}&order=desc&page=${this.ltPage}&per_page=25`
                );
                if (res.ok) {
                    this.largeTransfers = await res.json();
                }
            } catch (e) {
                console.error('Error loading large transfers:', e);
            }
        },

        changeLtPage(page) {
            this.ltPage = page;
            this.loadLargeTransfers();
        },

        updateCharts() {
            const ts = this.timeSeries;
            const labels = ts.map(d => d.label);
            const dc = window.divergentColor || ['#8b5cf6', '#10f7cf', '#ec4899', '#3b82f6', '#f59e0b', '#22c55e', '#ef4444'];

            // Stacked volume chart by program
            if (this.stackedPrograms.length > 0) {
                const datasets = this.stackedPrograms.map((prog, i) => ({
                    label: prog.name || this.formatPubkey(prog.program),
                    data: prog.timeSeries.map(d => d.volume),
                    backgroundColor: dc[i % dc.length] + '99',
                    borderColor: dc[i % dc.length],
                    borderWidth: 1
                }));
                const stackedLabels = this.stackedPrograms[0]?.timeSeries.map(d => d.label) || labels;
                this.charts.stackedVolume.data = { labels: stackedLabels, datasets };
            } else {
                this.charts.stackedVolume.data = {
                    labels,
                    datasets: [{
                        label: 'Transfer Volume (USD)',
                        data: ts.map(d => d.volume),
                        backgroundColor: dc[1] + '99',
                        borderColor: dc[1],
                        borderWidth: 1
                    }]
                };
            }
            this.charts.stackedVolume.update();

            // Transfers & Senders line chart
            this.charts.transfers.data = {
                labels,
                datasets: [
                    { label: 'Transfers', data: ts.map(d => d.transfers), borderColor: dc[0], yAxisID: 'y1' },
                    { label: 'Unique Senders', data: ts.map(d => d.signers), borderColor: dc[2], yAxisID: 'y2' }
                ]
            };
            this.charts.transfers.update();

            // Program doughnut
            const top8 = this.programs.slice(0, 8);
            this.charts.programDoughnut.data = {
                labels: top8.map(d => d.name || this.formatPubkey(d.program)),
                datasets: [{
                    data: top8.map(d => d.volume),
                    backgroundColor: dc.slice(0, top8.length)
                }]
            };
            this.charts.programDoughnut.update();

            // Token doughnut
            const topTokens = this.tokenBreakdown.slice(0, 8);
            this.charts.tokenDoughnut.data = {
                labels: topTokens.map(d => d.symbol),
                datasets: [{
                    data: topTokens.map(d => d.volume),
                    backgroundColor: dc.slice(0, topTokens.length)
                }]
            };
            this.charts.tokenDoughnut.update();

            // CEX time series chart
            this.$nextTick(() => {
                if (this.cexTimeSeries.length > 0 && this.$refs.cexTimeSeriesChart) {
                    if (!this.charts.cexTimeSeries) {
                        this.charts.cexTimeSeries = new Chart(this.$refs.cexTimeSeriesChart, {
                            type: 'bar',
                            data: { labels: [], datasets: [] },
                            options: {
                                ...getBarChartOptions({ formatCallback: v => '$' + this.formatCompact(Math.abs(v)) }),
                                scales: {
                                    x: { stacked: true },
                                    y: {
                                        stacked: true,
                                        ticks: { callback: v => '$' + this.formatCompact(Math.abs(v)) }
                                    },
                                    y2: {
                                        type: 'linear',
                                        position: 'right',
                                        grid: { drawOnChartArea: false },
                                        ticks: { callback: v => '$' + this.formatCompact(v) }
                                    }
                                }
                            }
                        });
                    }

                    const cexLabels = this.cexTimeSeries.map(d => d.label);
                    this.charts.cexTimeSeries.data = {
                        labels: cexLabels,
                        datasets: [
                            {
                                label: 'Deposits (In)',
                                data: this.cexTimeSeries.map(d => d.inflow),
                                backgroundColor: (dc[5] || '#22c55e') + 'b3',
                                borderColor: dc[5] || '#22c55e',
                                borderWidth: 1,
                                stack: 'flow'
                            },
                            {
                                label: 'Withdrawals (Out)',
                                data: this.cexTimeSeries.map(d => -d.outflow),
                                backgroundColor: (dc[6] || '#ef4444') + 'b3',
                                borderColor: dc[6] || '#ef4444',
                                borderWidth: 1,
                                stack: 'flow'
                            },
                            {
                                label: 'Net Flow',
                                type: 'line',
                                data: this.cexTimeSeries.map(d => d.netFlow),
                                borderColor: dc[3] || '#3b82f6',
                                borderWidth: 2,
                                pointRadius: 3,
                                fill: false,
                                yAxisID: 'y2'
                            }
                        ]
                    };
                    this.charts.cexTimeSeries.update();
                }
            });
        },

        formatTime(ts) {
            if (!ts) return '-';
            const d = new Date(ts);
            return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        },

        getLabelClass(label) {
            const l = (label || '').toLowerCase();
            if (l === 'cex' || l === 'exchange') return 'label-tag--cex';
            if (l === 'whale') return 'label-tag--whale';
            if (l === 'protocol') return 'label-tag--protocol';
            return '';
        },

        exportData() {
            const csv = [['Time', 'Volume', 'Transfers', 'Senders'].join(',')];
            this.timeSeries.forEach(r => {
                csv.push([r.label, r.volume, r.transfers, r.signers].join(','));
            });
            const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `transfers_${this.currentRange}.csv`;
            a.click();
        },

        capRange(range, maxRange) {
            const parseDays = r => {
                const m = r.match(/^(\d+)(h|d)$/);
                if (!m) return 30;
                return m[2] === 'h' ? parseFloat(m[1]) / 24 : parseInt(m[1]);
            };
            return parseDays(range) > parseDays(maxRange) ? maxRange : range;
        },

        formatPubkey(a) {
            return a?.length > 12 ? `${a.slice(0, 4)}..${a.slice(-4)}` : a;
        },

        formatCompact(v) {
            if (!v) return '0';
            if (v >= 1e12) return (v / 1e12).toFixed(2) + 'T';
            if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
            if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M';
            if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
            return Number(v).toLocaleString();
        }
    }
};
</script>

<style scoped>
.address-cell {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.address-link {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.85rem;
    color: var(--compass-text-primary, #e6edf3);
    text-decoration: none;
}

.address-link:hover {
    color: var(--compass-cyan, #10f7cf);
}

.entity-tag {
    font-size: 0.65rem;
    padding: 1px 6px;
    border-radius: var(--compass-radius-sm, 8px);
    background: rgba(139, 92, 246, 0.15);
    color: var(--compass-purple, #8b5cf6);
    white-space: nowrap;
}

.label-tag {
    font-size: 0.6rem;
    padding: 1px 5px;
    border-radius: 3px;
    background: rgba(107, 114, 128, 0.2);
    color: var(--compass-text-secondary, #7d8590);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
}

.label-tag--cex {
    background: rgba(245, 158, 11, 0.15);
    color: #f59e0b;
}

.label-tag--whale {
    background: rgba(59, 130, 246, 0.15);
    color: #3b82f6;
}

.label-tag--protocol {
    background: rgba(16, 247, 207, 0.15);
    color: var(--compass-cyan, #10f7cf);
}

.tx-link {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.8rem;
    color: var(--compass-text-secondary, #7d8590);
    text-decoration: none;
}

.tx-link:hover {
    color: var(--compass-cyan, #10f7cf);
}

.big-metric {
    font-size: 1.5rem;
    font-weight: 700;
    font-family: 'JetBrains Mono', monospace;
}

.metric-label {
    font-size: 0.8rem;
    color: var(--compass-text-secondary, #7d8590);
    margin-top: 0.25rem;
}

.entity-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

.entity-logo {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.entity-details {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.entity-pubkey {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: var(--compass-text-secondary, #7d8590);
    opacity: 0.7;
}

.compass-select--sm {
    font-size: 0.8rem;
    padding: 4px 8px;
    border-radius: 6px;
    background: var(--compass-bg-secondary, #161b22);
    color: var(--compass-text-primary, #e6edf3);
    border: 1px solid var(--compass-border, #30363d);
}
</style>
